'use no memo';

import { ATTRIBUTES as A } from 'helpers/analyticsAttributes';
import { ANALYTICS_EVENT_NAME as EVENTS, PLATFORM } from 'helpers/constants';

const CommonSchema = {
  events: {
    [EVENTS.selectCookieConsent]: [
      A.functionalCookiesAccepted,
      A.performanceCookiesAccepted,
      A.socialCookiesAccepted,
      A.targetingCookiesAccepted,
    ],
    [EVENTS.selectMenuItem]: [
      A.menuId,
      A.menuItemId,
      A.menuItemSource,
      A.menuItemLocation,
      A.restaurantLocationId,
      A.restaurantBrandId,
      A.itemHasPhoto,
      A.itemIsPopularItem,
      A.isEmbeddedSite,
      A.isDirectToMarketplaceRedirect,
    ],
    [EVENTS.upsellRailViewed]: [
      A.upsellItemCount,
      A.upsellRestaurantId,
      A.viewport,
    ],
    [EVENTS.upsellItemDetailViewed]: [
      A.upsellItemId,
      A.upsellItemName,
      A.upsellPositionIndex,
      A.viewport,
    ],
    [EVENTS.upsellItemError]: [
      A.upsellItemId,
      A.upsellItemName,
      A.upsellErrorReason,
      A.viewport,
    ],
    [EVENTS.upsellRecommendationTimeoutError]: [
      A.upsellRestaurantId,
      A.viewport,
    ],
  },
};

// Modify these as-needed if we find there are different attributes per-platform
export const DirectSchema = {
  platform: PLATFORM.direct,
  events: {
    ...CommonSchema.events,
    [EVENTS.addToCart]: [
      A.itemHasPhoto,
      A.itemHasNestedMods,
      A.menuId,
      A.menuItemSource,
      A.menuItemLocation,
      A.orderAheadDatetime,
      A.orderIsAsap,
      A.orderType,
      A.restaurantBrandId,
      A.restaurantLocationCategory,
      A.restaurantLocationId,
      A.restaurantLocationName,
      A.isDelete,
      A.isEmbeddedSite,
      A.isCartUpdate,
      A.isDirectToMarketplaceRedirect,
    ],
    [EVENTS.checkout]: [
      A.deliveryFeeAmount,
      A.hasDeliveryFee,
      A.hasMiscFee,
      A.isDirectToMarketplaceRedirect,
      A.isEmbeddedSite,
      A.managedDeliveryId,
      A.menuId,
      A.miscFeeAmount,
      A.orderAheadDatetime,
      A.orderIsAsap,
      A.orderIsReorder,
      A.orderType,
      A.paymentTypeDisplayed,
      A.restaurantBrandId,
      A.restaurantLocationCategory,
      A.restaurantLocationId,
      A.restaurantLocationName,
      A.totalAmount,
    ],
    [EVENTS.purchaseFailure]: [
      A.errorReason,
      A.paymentType,
      A.isEmbeddedSite,
      A.isDirectToMarketplaceRedirect,
      A.restaurantLocationId,
    ],
    [EVENTS.viewMenu]: [
      A.menuId,
      A.hasCoverPhoto,
      A.orderAheadDatetime,
      A.orderIsAsap,
      A.restaurantBrandId,
      A.restaurantLocationCategory,
      A.restaurantLocationId,
      A.restaurantLocationName,
      A.hasAutoPromo,
      A.url,
      A.isEmbeddedSite,
      A.isDirectToMarketplaceRedirect,
      A.isFromCnScript,
      A.platform,
      A.theme,
      A.utmSource,
      A.utmMedium,
      A.utmCampaign,
      A.externalReferrer,
    ],
  },
};

export const MarketplaceSchema = {
  platform: PLATFORM.marketplace,
  events: {
    ...CommonSchema.events,
    [EVENTS.addToCart]: [
      A.itemHasPhoto,
      A.itemHasNestedMods,
      A.menuId,
      A.menuItemSource,
      A.menuItemLocation,
      A.orderAheadDatetime,
      A.orderIsAsap,
      A.orderType,
      A.restaurantBrandId,
      A.restaurantLocationCategory,
      A.restaurantLocationId,
      A.restaurantLocationName,
      A.isDelete,
      A.isEmbeddedSite,
      A.isCartUpdate,
      A.isDirectToMarketplaceRedirect,
    ],
    [EVENTS.checkout]: [
      A.deliveryFeeAmount,
      A.hasDeliveryFee,
      A.hasMarketplaceDinerFee,
      A.hasMiscFee,
      A.isDirectToMarketplaceRedirect,
      A.managedDeliveryId,
      A.marketplaceDinerFeeAmount,
      A.menuId,
      A.miscFeeAmount,
      A.orderAheadDatetime,
      A.orderIsAsap,
      A.orderIsReorder,
      A.orderType,
      A.paymentTypeDisplayed,
      A.restaurantBrandId,
      A.restaurantLocationCategory,
      A.restaurantLocationId,
      A.restaurantLocationName,
      A.totalAmount,
    ],
    [EVENTS.purchaseFailure]: [
      A.errorReason,
      A.paymentType,
      A.isDirectToMarketplaceRedirect,
      A.restaurantLocationId,
    ],
    [EVENTS.selectSaveRestaurant]: [A.isSaved, A.restaurantLocationId],
    [EVENTS.viewMenu]: [
      A.menuId,
      A.hasCoverPhoto,
      A.orderAheadDatetime,
      A.orderIsAsap,
      A.restaurantBrandId,
      A.restaurantLocationCategory,
      A.restaurantLocationId,
      A.restaurantLocationName,
      A.hasAutoPromo,
      A.url,
      A.isDirectToMarketplaceRedirect,
      A.isFromCnScript,
      A.platform,
      A.theme,
      A.utmSource,
      A.utmMedium,
      A.utmCampaign,
      A.externalReferrer,
    ],
  },
};

export const YelpSchema = {
  platform: PLATFORM.yelp,
  events: {
    ...CommonSchema.events,
    [EVENTS.addToCart]: [
      A.itemHasPhoto,
      A.menuId,
      A.menuItemSource,
      A.menuItemLocation,
      A.orderAheadDatetime,
      A.orderIsAsap,
      A.orderType,
      A.restaurantBrandId,
      A.restaurantLocationCategory,
      A.restaurantLocationId,
      A.restaurantLocationName,
    ],
    [EVENTS.checkout]: [
      A.deliveryFeeAmount,
      A.hasDeliveryFee,
      A.hasMiscFee,
      A.menuId,
      A.miscFeeAmount,
      A.orderAheadDatetime,
      A.orderIsAsap,
      A.orderType,
      A.restaurantBrandId,
      A.restaurantLocationCategory,
      A.restaurantLocationId,
      A.restaurantLocationName,
    ],
    [EVENTS.purchaseFailure]: [A.errorReason, A.paymentType],
    [EVENTS.selectMenuItem]: [
      A.menuId,
      A.menuItemId,
      A.menuItemSource,
      A.menuItemLocation,
      A.restaurantLocationId,
      A.restaurantBrandId,
      A.itemHasPhoto,
      A.itemIsPopularItem,
    ],
    [EVENTS.viewMenu]: [
      A.menuId,
      A.orderAheadDatetime,
      A.orderIsAsap,
      A.restaurantBrandId,
      A.restaurantLocationCategory,
      A.restaurantLocationId,
      A.restaurantLocationName,
      A.url,
      A.isFromCnScript,
      A.platform,
      A.theme,
      A.utmSource,
      A.utmMedium,
      A.utmCampaign,
      A.externalReferrer,
    ],
  },
};
